//ErrorsAndOmission.tsx
import React, {useEffect, useState} from "react";
import {useAppraiserApi, useFileApi} from "../../../context/ApiContext.tsx";
import {NotificationType, useNotification} from "../../../context/NotificationContext.tsx";
import {formatDateForInput} from "../../../utils/dateUtils.ts";
import {AppraiserProfileFile} from "../../../api/ApiTypes.ts";
import {FileDownloadType, FileViewer} from "../../../components/FileViewer.tsx";
import {AppraiserProfileTabProps} from "./AppraiserProfile.tsx";

interface EandOFormData {
    provider: string;
    perIncidentAmount: number;
    aggregateAmount: number;
    expirationDate: string;
    policyFile: File | null;
    appraiserId: number;
}

export const ErrorsAndOmissions: React.FC<AppraiserProfileTabProps> = ({appraiserInfo}) => {
    const [formData, setFormData] = useState<EandOFormData>({
        provider: appraiserInfo.enoProvider || '',
        perIncidentAmount: appraiserInfo.enoPerIncidentAmount || 0,
        aggregateAmount: appraiserInfo.enoAggregateAmount || 0,
        expirationDate: formatDateForInput(appraiserInfo.enoExpirationDate),
        policyFile: null,
        appraiserId: appraiserInfo.id
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [currentFile, setCurrentFile] = useState<AppraiserProfileFile | null>(null);
    const { showNotification } = useNotification();

    const appraiserApi = useAppraiserApi();
    const fileApi = useFileApi();

    useEffect(() => {
        if (!appraiserInfo) return;
        fetchEoFile(appraiserInfo.id)
    }, []);

    const fetchEoFile = async (appraiserId: number) => {
        try {
            const file = await fileApi.getMyEoFile(appraiserId);
            if (file) {
                setCurrentFile(file);
            }
        } catch (error) {
            console.error('Failed to fetch E&O file:', error);
            // Don't show notification for this, as it might be normal to not have a file yet
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type } = e.target;

        if (type === 'file') {
            const fileList = e.target.files;
            if (fileList && fileList.length > 0) {
                setFormData(prev => ({
                    ...prev,
                    policyFile: fileList[0]
                }));
            }
        } else if (type === 'number') {
            // Handle number inputs properly
            setFormData(prev => ({
                ...prev,
                [name]: value === '' ? 0 : Number(value)
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData.policyFile) {
            showNotification(NotificationType.ERROR, 'Please upload an E&O policy file to update information');
            return;
        }

        setIsSubmitting(true);

        try {
            const formDataToSend = new FormData();
            formDataToSend.append('provider', formData.provider);
            formDataToSend.append('perIncidentCoverage', formData.perIncidentAmount.toString());
            formDataToSend.append('aggregateCoverage', formData.aggregateAmount.toString());
            formDataToSend.append('expirationDate', formData.expirationDate);
            formDataToSend.append('policyFile', formData.policyFile);
            formDataToSend.append('appraiserId', formData.appraiserId.toString());

            const appraiserInfo = await appraiserApi.updateEandO(formDataToSend);

            setFormData({
                provider: appraiserInfo.enoProvider || '',
                perIncidentAmount: appraiserInfo.enoPerIncidentAmount || 0,
                aggregateAmount: appraiserInfo.enoAggregateAmount || 0,
                expirationDate: formatDateForInput(appraiserInfo.enoExpirationDate),
                policyFile: null,
                appraiserId: appraiserInfo.id
            });

            // Refresh the file information after update
            if (appraiserInfo.id) {
                fetchEoFile(appraiserInfo.id);
            }

            showNotification(NotificationType.SUCCESS, 'E&O information updated successfully');
        } catch (error) {
            console.error('Failed to update E&O information:', error);
            showNotification(NotificationType.ERROR, 'Failed to update E&O information');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div>
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                <p className="text-sm text-blue-700">
                    <strong>Note:</strong> You must upload a new E&O policy file to update your information. All fields will be editable after selecting a file.
                </p>
            </div>

            {currentFile && (
                <div className="mb-4 p-3 border border-gray-200 rounded-md bg-gray-50">
                    <div className="flex flex-wrap items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <span className="font-medium text-gray-800">Current E&O Policy:</span>
                            <span title="Filename" className="text-sm">{currentFile.fileName}</span>
                            <span title="Upload Date" className="text-sm text-gray-600">{new Date(currentFile.uploadDate).toLocaleDateString()}</span>
                        </div>
                        <FileViewer fileId={currentFile.id} fileIs={FileDownloadType.APPRAISER_PROFILE} />
                    </div>
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                            Expiration Date (MM/DD/YYYY)
                        </label>
                        <input
                            type="date"
                            name="expirationDate"
                            value={formData.expirationDate}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-slate-300 rounded-md text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            disabled={!formData.policyFile}
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                            E&O Policy File
                        </label>
                        <div className="mt-1 flex items-center">
                            <label className="w-full flex items-center justify-center px-6 py-2 border border-slate-300 rounded-md cursor-pointer hover:bg-slate-50 bg-white">
                                <span className="text-sm text-slate-600">
                                    {formData.policyFile ? formData.policyFile.name : 'Choose file'}
                                </span>
                                <input
                                    type="file"
                                    name="policyFile"
                                    onChange={handleInputChange}
                                    className="hidden"
                                    accept=".pdf,.doc,.docx"
                                    required
                                />
                            </label>
                        </div>
                        <p className="mt-1 text-xs text-slate-500">Upload your current E&O policy document (PDF, DOC, DOCX)</p>
                    </div>
                </div>

                <div className="flex justify-end">
                    <button
                        type="submit"
                        disabled={isSubmitting || !formData.policyFile}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isSubmitting ? 'Updating...' : 'Update E&O Information'}
                    </button>
                </div>
            </form>
        </div>
    );
};